import { prettifyMixedCase } from '@frontend/shared/Util.js';

export const resolveRefs = (schema, root) => {
  if (Array.isArray(schema)) {
    return schema.map((s) => resolveRefs(s, root));
  }

  if (typeof schema === 'object' && schema !== null) {
    if (schema.$ref) {
      const path = schema.$ref.replace(/^#\//, '').split('/');
      let target = root;

      for (const segment of path) {
        if (target && segment in target) {
          target = target[segment];
        } else {
          console.warn(`Could not resolve $ref: ${schema.$ref}`);
          return {};
        }
      }

      return resolveRefs(target, root);
    } else {
      const resolved = {};
      for (const [k, v] of Object.entries(schema)) {
        resolved[k] = resolveRefs(v, root);
      }
      return resolved;
    }
  }

  return schema;
};

export function deepClone(value) {
  if (Array.isArray(value)) {
    return value.map(deepClone);
  }

  if (value && typeof value === 'object') {
    const cloned = {};
    for (const [k, v] of Object.entries(value)) {
      cloned[k] = deepClone(v);
    }
    return cloned;
  }

  return value;
}

export const getInitialValue = (prop, draftValue, deepClone) => {
  const effectiveProp = prop?.anyOf ?
    prop.anyOf.find((opt) => opt.type !== 'null') ?? prop.anyOf[0] :
    prop;

  if (effectiveProp?.type === 'object' && effectiveProp.properties) {
    const obj = {};
    for (const [k, v] of Object.entries(effectiveProp.properties)) {
      obj[k] = getInitialValue(v, draftValue?.[k], deepClone);
    }
    return obj;
  }

  if (effectiveProp?.type === 'array') {
    return Array.isArray(draftValue) ?
      draftValue.map((item) => deepClone(item)) :
      [];
  }

  if (effectiveProp?.enum) {
    return draftValue ?? effectiveProp.enum[0] ?? '';
  }

  switch (effectiveProp?.type) {
    case 'string':
      return draftValue ?? '';
    case 'number':
    case 'integer':
      return draftValue ?? 0;
    case 'boolean':
      return draftValue ?? false;
    case 'object':
      return deepClone(draftValue);
    default:
      return draftValue ?? null;
  }
};

export function resolveSchema(schema) {
  if (schema?.anyOf) {
    return schema.anyOf.find((opt) => opt.type !== 'null') ?? schema.anyOf[0];
  }
  return schema;
}

export function getValueType(schema) {
  const res = resolveSchema(schema);

  if (res?.enum) return 'enum';
  if (res?.type === 'array') return 'array';
  if (res?.type === 'object' || res?.properties || res?.anyOf) return 'object';

  if (['string', 'number', 'integer', 'boolean'].includes(res?.type)) {
    return res.type;
  }

  return 'unknown';
}

export function getInputType(schema) {
  const res = resolveSchema(schema);
  if (['number', 'integer'].includes(res?.type)) return 'number';
  if (res?.type === 'boolean') return 'checkbox';
  return 'text';
}

export function isEmptyValue(value) {
  return value === null || value === '' || value === undefined;
}

export function normalizeValue(val) {
  return typeof val === 'string' ? val.trim().toLowerCase() : String(val ?? '');
}

export function computeChangeType(schema, current, draft) {
  const res = resolveSchema(schema);

  if (!['string', 'number', 'integer', 'boolean'].includes(res?.type)) {
    return '';
  }

  const currEmpty = isEmptyValue(current);
  const draftEmpty = isEmptyValue(draft);

  if (currEmpty && draftEmpty) return 'both_empty';
  if (currEmpty && !draftEmpty) return 'addition';
  if (!currEmpty && draftEmpty) return 'removal';

  return normalizeValue(current) !== normalizeValue(draft) ? 'change' : 'same';
}

export function orderProperties(schema) {
  const res = resolveSchema(schema);
  const props = res?.properties;
  if (!props) return {};

  return Object.fromEntries(
      Object.entries(props).sort(([_, v]) => {
        if (v?.type === 'array') return 2;
        if (v?.type === 'object' || v?.properties || v?.anyOf) return 1;
        return 0;
      }),
  );
}

export function range0(n) {
  return Array.from({ length: Math.max(0, n) }, (_, i) => i);
}

export function getValueAtPath(obj, path) {
  return path.reduce((acc, key) => (acc == null ? undefined : acc[key]), obj);
}

export function flattenSchema(section, schema, dossier, path = [], items = [], isArrayItem = false) {
  const id = path[path.length - 1];
  const label = prettifyMixedCase(id);
  const value = getValueAtPath(dossier, path);

  for (const keyword of ['anyOf', 'oneOf', 'allOf']) {
    if (Array.isArray(schema[keyword])) {
      const nonNullOptions = schema[keyword].filter(
          (opt) => opt.type !== 'null',
      );

      nonNullOptions.forEach((option, index) => {
        const fullPath = [...path];
        // Go to single option
        flattenSchema(section, option, dossier, fullPath, items);
      });
    }
  }

  // Option is an object
  if (schema.properties) {
    const item = {
      section,
      id,
      label,
      path,
      pathString: path.join('.'),
      type: 'object',
      show: false,
      required: false,
      isScalar: false,
      isArrayItem,
    };

    items.push(item);

    for (const [key, prop] of Object.entries(schema.properties)) {
      const fullPath = [...path, key];

      // Walk each entry
      flattenSchema(section, prop, dossier, fullPath, items);
    }
  }

  // Option is an array
  if (schema.items) {
    const item = {
      section,
      id,
      label,
      path,
      pathString: path.join('.'),
      type: 'array',
      show: false,
      required: false,
      isScalar: false,
      isArrayItem,
    };

    items.push(item);

    flattenSchema(section, schema.items, dossier, [...path, 0], items, true);
  }

  if (['boolean', 'integer', 'string'].includes(schema.type)) {
    const item = {
      section,
      id,
      label,
      path,
      pathString: path.join('.'),
      type: schema.type,
      show: false,
      required: false,
      value,
      isScalar: true,
      isArrayItem,
    };

    items.push(item);
  }

  return items;
}

export function expandPathAncestors(pathStrings) {
  const result = [];

  for (const path of pathStrings) {
    const parts = path.split('.');

    for (let i = 1; i <= parts.length; i++) {
      const ancestor = parts.slice(0, i).join('.');
      if (!result.includes(ancestor)) {
        result.push(ancestor);
      }
    }
  }

  return result;
}
